import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import Avatar from './Avatar';
import { useModal } from '../context/ModalContext';
import '../styles/ManagementPanel.css';

const ManagementPanel = ({ user, dashboardData, onRefresh }) => {
  const modal = useModal();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('user_management_active_tab') || 'operators';
  });
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    localStorage.setItem('user_management_active_tab', activeTab);
  }, [activeTab]);
  const [editForm, setEditForm] = useState({ notification_email: '', role: '' });
  const [saving, setSaving] = useState(false);

  // Check if current user is super admin or admin
  const isSuperAdmin = user?.is_super_admin || false;
  const isAdmin = user?.is_admin || false;

  // Get operators, clients, and admins from dashboardData and sort alpha-numerically
  const operators = (dashboardData?.data?.operators || []).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  const clients = (dashboardData?.data?.clients || []).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  const admins = (dashboardData?.data?.admins || []).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );

  // Open edit modal
  const handleEditUser = (userToEdit) => {
    // Determine current role
    let currentRole = 'client';
    if (userToEdit.is_admin) {
      currentRole = 'admin';
    } else if (userToEdit.is_operator) {
      currentRole = 'operator';
    }

    setEditingUser(userToEdit);
    setEditForm({
      notification_email: userToEdit.invitation_email || userToEdit.email || '',
      role: currentRole
    });
  };

  // Save notification email and role
  const handleSaveUser = async () => {
    if (!editForm.notification_email || !editForm.notification_email.trim()) {
      modal.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      // Determine original role
      let originalRole = 'client';
      if (editingUser.is_admin) {
        originalRole = 'admin';
      } else if (editingUser.is_operator) {
        originalRole = 'operator';
      }

      // Update notification email
      await axios.post('/api/admin/update-user-notification-email', {
        user_id: editingUser._id,
        notification_email: editForm.notification_email.trim(),
        agency_id: editingUser.agency_id
      });

      // Update role if changed
      if (editForm.role !== originalRole) {
        await axios.post('/api/super-admin/change-user-role', {
          user_id: editingUser._id,
          new_role: editForm.role,
          agency_id: editingUser.agency_id
        });
      }

      modal.success('User updated successfully');
      setEditingUser(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update user';
      modal.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Toggle user status (enable/disable)
  const handleToggleUserStatus = async (userToToggle) => {
    const isDisabled = userToToggle.status === 'disabled';
    const action = isDisabled ? 'enable' : 'disable';

    // Show confirmation for both enable and disable
    const confirmMessage = isDisabled
      ? `Are you sure you want to re-enable ${userToToggle.name}?\nThey will be able to log in again.`
      : `Are you sure you want to disable ${userToToggle.name}?\nThey will be logged out immediately and cannot log in until re-enabled.`;
    const confirmTitle = isDisabled ? 'Enable User' : 'Disable User';

    const result = await modal.confirm(confirmMessage, confirmTitle);
    if (!result) return;

    try {
      const endpoint = `/api/super-admin/${action}-user`;
      const response = await axios.post(endpoint, {
        user_id: userToToggle._id,
        agency_id: userToToggle.agency_id
      });

      if (response.data.success) {
        modal.success(`User ${isDisabled ? 'enabled' : 'disabled'} successfully`);
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      modal.error(error.response?.data?.error || `Failed to ${action} user`);
    }
  };

  // Delete user
  const handleDeleteUser = async (userToDelete) => {
    const result = await modal.confirm(
      `Are you sure you want to delete ${userToDelete.name}?\nThis action cannot be undone.`,
      'Delete User'
    );
    if (!result) return;

    try {
      const response = await axios.delete('/api/super-admin/delete-user', {
        data: {
          email: userToDelete.email || userToDelete.invitation_email,
          agency_id: userToDelete.agency_id
        }
      });

      if (response.data.success) {
        modal.success(`User ${userToDelete.name} deleted successfully`);
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      modal.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const currentUsers = activeTab === 'operators' ? operators : activeTab === 'clients' ? clients : admins;

  return (
    <div className="management-panel">
      <div className="content-section">
        {/* Tabs */}
        <div className="management-tabs">
          <button
            className={`tab-btn ${activeTab === 'operators' ? 'active' : ''}`}
            onClick={() => setActiveTab('operators')}
          >
            Operators <span className="count-badge">{operators.length}</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveTab('clients')}
          >
            Clients <span className="count-badge">{clients.length}</span>
          </button>
          {(isSuperAdmin || isAdmin) && (
            <button
              className={`tab-btn ${activeTab === 'admins' ? 'active' : ''}`}
              onClick={() => setActiveTab('admins')}
            >
              Admins <span className="count-badge">{admins.length}</span>
            </button>
          )}
        </div>

        {/* User Table */}
        {currentUsers.length === 0 ? (
          <div className="no-users-message">
            <p>No {activeTab} found. Use the Invitations tab to invite {activeTab}.</p>
          </div>
        ) : (
          <div className="management-table-container">
            <table className="management-table">
              <thead>
                <tr>
                  <th style={{width: '60px'}}></th>
                  <th>Name</th>
                  <th>Login Email</th>
                  <th>Notification Email</th>
                  <th style={{width: '120px'}}>Status</th>
                  <th style={{width: '150px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map(u => (
                  <tr key={u._id}>
                    <td>
                      <Avatar user={u} size="sm" />
                    </td>
                    <td className="name-cell">{u.name || u.email || 'Unknown'}</td>
                    <td className="email-cell">{u.email || 'N/A'}</td>
                    <td className="email-cell">{u.invitation_email || u.email || 'N/A'}</td>
                    <td className="management-status-cell">
                      {u.status === 'disabled' && (
                        <span className="management-user-status-badge disabled">Disabled</span>
                      )}
                      {u.status === 'pending' && (
                        <span className="management-user-status-badge pending">Pending</span>
                      )}
                      {(!u.status || u.status === 'active') && (
                        <span className="management-user-status-badge active">Active</span>
                      )}
                    </td>
                    <td className="management-action-cell">
                      <button
                        onClick={() => handleToggleUserStatus(u)}
                        className={`management-action-btn ${u.status === 'disabled' ? 'enable' : 'disable'}`}
                        title={u.status === 'disabled' ? 'Enable user' : 'Disable user'}
                      >
                        <Icon name={u.status === 'disabled' ? 'check' : 'unlink'} size="1rem" />
                      </button>
                      <button onClick={() => handleEditUser(u)} className="management-action-btn edit" title="Edit User">
                        <Icon name="pencil" size="1rem" />
                      </button>
                      <button onClick={() => handleDeleteUser(u)} className="management-action-btn delete" title="Delete user">
                        <Icon name="trash-empty" size="1rem" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => !saving && setEditingUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit User {editingUser.name}</h3>
              <button className="modal-close-btn" onClick={() => !saving && setEditingUser(null)} aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Login Email (Read-only)</label>
                <input
                  type="text"
                  value={editingUser.email}
                  disabled
                  className="readonly-input"
                />
                <small>This is the OAuth email used for signing in</small>
              </div>
              <div className="form-group">
                <label>Notification Email</label>
                <input
                  type="email"
                  value={editForm.notification_email}
                  onChange={(e) => setEditForm({...editForm, notification_email: e.target.value})}
                  placeholder="Enter notification email"
                  disabled={saving}
                />
                <small>Email address where notifications will be sent</small>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  disabled={saving || (editingUser.is_admin && !isSuperAdmin)}
                >
                  <option value="client">Client</option>
                  <option value="operator">Operator</option>
                  {isSuperAdmin && <option value="admin">Admin</option>}
                </select>
                <small>
                  {isSuperAdmin
                    ? 'Change the user\'s role. Role changes preserve org/brand assignments between Client and Operator.'
                    : editingUser.is_admin
                      ? 'Only Super Admins can change Admin roles'
                      : 'Change between Client and Operator roles'
                  }
                </small>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => !saving && setEditingUser(null)}
                className="cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="save-btn-modal"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementPanel;
